import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserGroupIcon, ChartBarIcon, CogIcon } from '@heroicons/react/24/outline';
import useAdminApi from '../hooks/useAdminApi';

// Sections
import UsersSection from '../components/admin/sections/UsersSection';
import OrganizationsSection from '../components/admin/sections/OrganizationsSection';
import ScenariosSection from '../components/admin/sections/ScenariosSection';
import AchievementsSection from '../components/admin/sections/AchievementsSection';
import TariffsSection from '../components/admin/sections/TariffsSection';
import StatsSection from '../components/admin/sections/StatsSection';
import PromptTemplatesSection from '../components/admin/sections/PromptTemplatesSection';
import SettingsSection from '../components/admin/sections/SettingsSection';

const Admin = () => {
  const api = useAdminApi();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Shared data
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [promptTemplates, setPromptTemplates] = useState([]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, orgsData, scenariosData, tariffsData, templatesData] = await Promise.all([
        api.fetchUsers(),
        api.fetchOrganizations(),
        api.fetchScenarios(),
        api.fetchTariffs(),
        api.fetchPromptTemplates()
      ]);
      setUsers(usersData);
      setOrganizations(orgsData);
      setScenarios(scenariosData);
      setTariffs(tariffsData);
      setPromptTemplates(templatesData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAllData();
  }, []);

  const handleRefresh = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-gray-900 py-12 flex items-center justify-center transition-colors">
        <div className="text-gray-600 dark:text-gray-300 text-lg">Загрузка панели...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-gray-900 py-12 flex items-center justify-center transition-colors">
        <div className="text-red-600 dark:text-red-400 text-lg">Ошибка: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-950 py-12 flex flex-col items-center transition-all">
      {/* Breadcrumbs */}
      <nav className="w-full max-w-[1200px] mb-2 sm:mb-4 text-xs sm:text-sm px-2">
        <ol className="flex flex-wrap items-center space-x-1 sm:space-x-2 text-gray-500 dark:text-gray-400">
          <li>
            <Link to="/" className="hover:underline text-blue-600 dark:text-blue-400">Главная</Link>
          </li>
          <li className="text-gray-400 dark:text-gray-500">/</li>
          <li className="text-gray-700 dark:text-gray-200 font-semibold">Админ-панель</li>
        </ol>
      </nav>

      {/* Header */}
      <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4 text-center drop-shadow-lg">
        Админ-панель
      </h1>
      <p className="text-sm sm:text-base md:text-lg text-gray-700 dark:text-gray-300 mb-6 sm:mb-10 text-center">
        Управление сайтом
      </p>

      {/* Main Content */}
      <div className="w-full max-w-full md:max-w-[900px] flex flex-col gap-6 sm:gap-8 mb-20 sm:mb-12 px-1 sm:px-2 md:px-0">
        
        {/* Users & Moderators */}
        <UsersSection 
          api={api} 
          organizations={organizations} 
          tariffs={tariffs} 
          onRefresh={handleRefresh} 
        />

        {/* Organizations */}
        <OrganizationsSection 
          api={api} 
          scenarios={scenarios} 
          tariffs={tariffs} 
          promptTemplates={promptTemplates}
          onRefresh={handleRefresh} 
        />

        {/* Tariffs & Payments */}
        <TariffsSection 
          api={api} 
          onRefresh={handleRefresh} 
        />

        {/* Settings */}
        <SettingsSection api={api} />

        {/* Prompt Templates */}
        <PromptTemplatesSection 
          api={api} 
          onRefresh={handleRefresh} 
        />

        {/* Achievements */}
        <AchievementsSection 
          api={api} 
          users={users}
          organizations={organizations}
          onRefresh={handleRefresh} 
        />

        {/* Scenarios */}
        <ScenariosSection 
          api={api} 
          organizations={organizations} 
          promptTemplates={promptTemplates} 
          onRefresh={handleRefresh} 
        />

        {/* Stats */}
        <StatsSection api={api} />
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg z-40 flex justify-around md:hidden">
        <button 
          onClick={() => { 
            setActiveTab('users'); 
            document.querySelector('[data-section="users"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
          }} 
          className={`flex flex-col items-center py-2 px-3 flex-1 ${activeTab === 'users' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <UserGroupIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Пользователи</span>
        </button>
        <button 
          onClick={() => { 
            setActiveTab('organizations'); 
            document.querySelector('[data-section="organizations"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
          }} 
          className={`flex flex-col items-center py-2 px-3 flex-1 ${activeTab === 'organizations' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <UserGroupIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Организации</span>
        </button>
        <button 
          onClick={() => { 
            setActiveTab('achievements'); 
            document.querySelector('[data-section="achievements"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
          }} 
          className={`flex flex-col items-center py-2 px-3 flex-1 ${activeTab === 'achievements' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <ChartBarIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Достижения</span>
        </button>
        <button 
          onClick={() => { 
            setActiveTab('scenarios'); 
            document.querySelector('[data-section="scenarios"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
          }} 
          className={`flex flex-col items-center py-2 px-3 flex-1 ${activeTab === 'scenarios' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <ChartBarIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Сценарии</span>
        </button>
        <button 
          onClick={() => { 
            setActiveTab('stats'); 
            document.querySelector('[data-section="stats"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
          }} 
          className={`flex flex-col items-center py-2 px-3 flex-1 ${activeTab === 'stats' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <CogIcon className="w-6 h-6 mb-1" />
          <span className="text-xs">Статистика</span>
        </button>
      </nav>
      {/* Ссылки на инструменты */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">Инструменты разработчика</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a href="/apidocs/" target="_blank" rel="noopener noreferrer" className="block p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
            <div className="text-2xl mb-2">📚</div>
            <div className="font-medium text-blue-700 dark:text-blue-300">Swagger API</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Документация REST API с возможностью тестирования</div>
          </a>
          <a href="http://217.198.9.197:3001" target="_blank" rel="noopener noreferrer" className="block p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition">
            <div className="text-2xl mb-2">📊</div>
            <div className="font-medium text-green-700 dark:text-green-300">Grafana</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Дашборды мониторинга и визуализация метрик</div>
          </a>
          <a href="http://217.198.9.197:9090" target="_blank" rel="noopener noreferrer" className="block p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/40 transition">
            <div className="text-2xl mb-2">🔥</div>
            <div className="font-medium text-orange-700 dark:text-orange-300">Prometheus</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Сбор и хранение метрик, алерты</div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Admin;
